import { Component } from '@angular/core';
import { WizardPageComponent } from '../../../../../../core/wizard/wizard-page-component';
import type { CompetitiveAnalysisDetailsWizard } from '../competitive-analysis-details-wizard';
import { StoreComparison } from '../../../../../../core/store/ranking/store-comparison';
import { StoreRanking } from '../../../../../../core/store/ranking/store-ranking';
import { Observable, Subject } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { StoreComparisonService } from '../../../../../../core/store/ranking/store-comparison.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as _ from 'lodash';
import { convertToNth } from 'src/app/core/util/string-utils';
import { RegionType } from 'src/app/core/location/region-type.enum';
import * as chroma from 'chroma-js';
import {
  buildRankingDescriptor,
  buildStoreRankingGroupDescriptor,
  buildStoreRankingTypeDescriptor
} from '../../comparison-utils';
import { ComparisonType } from '../../../../../../core/store/ranking/comparison-type.enum';
import { Definition } from '../../../../../../core/definition/definition';
import { FitType } from '../../../../../../core/store/fit/fit-type.enum';
import { DefinitionService } from '../../../../../../core/definition/definition.service';
import { AxisMode } from '../../../../../../core/chart/number-line-chart/AxisMode';
import { ChartAxisBuilder } from '../../../../../../core/chart/number-line-chart/chart-axis-builder';
import { NumberLineDataset } from '../../../../../../core/chart/number-line-chart/number-line-dataset';
import { ToastService } from '../../../../../../core/toast/toast.service';
import { FeatureCollection, Store } from '../../../../../../core/models';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { DetailState, selectDetailState } from 'src/app/detail/detail-state';
import { Store as NgrxStore } from '@ngrx/store';
import { AppState } from 'src/app/app-state';
import { KeyIndicatorType } from 'src/app/core/user-preferences/key-indicator-type.enum';
import { SalesSqftDisplayType } from 'src/app/core/user-preferences/sales-sqft-display-type.enum';
import { SalesVolumeDisplayType } from 'src/app/core/user-preferences/sales-volume-display-type.enum';
import { SpinnerSize } from 'src/app/core/util/spinner/spinner-size.enum';
@Component({
  standalone: false,
  selector: 'mtn-competitive-analysis-details-page',
  templateUrl: './competitive-analysis-details-page.component.html',
  styleUrls: ['./competitive-analysis-details-page.component.scss']
})
export class CompetitiveAnalysisDetailsPageComponent extends WizardPageComponent<CompetitiveAnalysisDetailsWizard> {

  private readonly GRADIENT = chroma.scale(['#EF4444', '#F97316', '#26C066']); // red → orange → green
  private readonly VALIDATORS_DRIVE_TIME = [Validators.min(1), Validators.max(20)];
  private readonly VALIDATORS_RING = [Validators.min(1), Validators.max(10)];

  readonly excludedStoresTooltip = `Store(s) Excluded from Ranking

One or more stores were excluded from the ranking due to missing data. This typically occurs when the store is in a rural area, or represents an insignificant impact to the market. See table below for details.
  `;

  key = 'competitive-analysis-details';

  comparison: StoreComparison;
  ranking: StoreRanking;

  datasets: NumberLineDataset[] = [];
  numberChartAxisBuilder: ChartAxisBuilder;

  isLoading = false;
  isRelativeMode = true;
  labelComparisonType: string;
  labelPercentile: string;
  labelRegion: string;
  marketArea: FeatureCollection;
  rankingDescriptor: string;
  rankingGroupDescriptor: string;
  rankingTypeDescriptor: string;
  RegionType = RegionType;
  SpinnerSize = SpinnerSize;

  isChartView = false;
  // ✅ New properties for SVG circular chart
  color: string;
  strokeDasharray: string = '';
  strokeDashoffset: string = '';
  tableData: any[] | null = null; // null = not loaded yet, [] = loaded but empty
  rankings: StoreRanking[] = [];
  store: Store;
  marketName: any;
  private cancelPendingRequest$ = new Subject<void>();


  constructor(private definitionService: DefinitionService,
    private storeComparisonService: StoreComparisonService,
    private hitTrackingService: HitTrackingService,
    private toaster: ToastService,
    protected ngrxStore: NgrxStore<AppState>) {
    super();
    this.title = 'Sales Performance Detail';
    this.closeButtonText = 'Close';
    this.subscribeToDetailState();
  }

  private subscribeToDetailState(): void {
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .subscribe((state: DetailState) => {
          this.store = state.store;
          this.marketName = state.marketArea.getFeature().properties['name'];
        })
    );
  }
  private initNumChart() {
    this.datasets = [];
    const ds = new NumberLineDataset(this.comparison.store, this.comparison.rankings)

    const mode = this.isRelativeMode ? AxisMode.RELATIVE : AxisMode.ABSOLUTE;
    const axisBuilder = new ChartAxisBuilder(mode, this.comparison.keyIndicator);
    axisBuilder.extend(ds.minValue);
    axisBuilder.extend(ds.maxValue);
    this.numberChartAxisBuilder = axisBuilder;

    this.datasets.push(ds)
  }

  onLoad(): Observable<any> {
    this.comparison = new StoreComparison(this.wizard.model.comparison);
    this.marketArea = this.wizard.model.marketArea;
    this.chartAndTableToggle = true;
    this.title =`<svg class="chart-icon" width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.72925 13.9861V17.9861M16.7292 11.9861V17.9861M12.7292 7.98615V17.9861M8.52925 21.9861H16.9292C18.6094 21.9861 19.4495 21.9861 20.0912 21.6592C20.6557 21.3715 21.1146 20.9126 21.4023 20.3481C21.7292 19.7064 21.7292 18.8663 21.7292 17.1861V8.78615C21.7292 7.10599 21.7292 6.26591 21.4023 5.62417C21.1146 5.05969 20.6557 4.60075 20.0912 4.31313C19.4495 3.98615 18.6094 3.98615 16.9292 3.98615H8.52925C6.84909 3.98615 6.00901 3.98615 5.36728 4.31313C4.80279 4.60075 4.34385 5.05969 4.05623 5.62417C3.72925 6.26591 3.72925 7.10599 3.72925 8.78614V17.1861C3.72925 18.8663 3.72925 19.7064 4.05623 20.3481C4.34385 20.9126 4.80279 21.3715 5.36728 21.6592C6.00901 21.9861 6.84909 21.9861 8.52925 21.9861Z" stroke="#48D47C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg> Competitive Analysis (${this.comparison.store.name}${this.comparison.store.number ? ' - ' + this.comparison.store.number : ''})`;

    this.initForm();
    this.setComparisonTypeLabel();

    if (this.comparison?.rankings?.length > 0) {
      this.updateChartDataFromRankings(this.comparison.rankings);
      this.mapRankingsToTableData(this.comparison.rankings);
    } else {
      this.fetchUnifiedRankingData();
    }

    return super.onLoad();
  }

  onToggleChart(type) :any{
   let viewtype;
   if(type === 'chart')
    {this.isChartView = true; viewtype='Chart view';}
    else
    {
      this.isChartView = false;
      viewtype='Table view';

    }

    const metadata ={
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      viewOpened: viewtype
    };
    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.STORE_SALES_PERFORMANCE_PANEL,
        metadata,
        USER_TRACKING_ACTIONS.RBS_CHART_CHANGED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_CHART_CHANGED],
        true
      ).subscribe();
  }
  private initForm(): void {
    this.form = new UntypedFormGroup({
      comparisonType: new UntypedFormControl(this.comparison.comparisonType, [Validators.required]),
      driveTimeMinutes: new UntypedFormControl(this.comparison.driveTimeMinutes || 7, this.VALIDATORS_DRIVE_TIME),
      keyIndicator: new UntypedFormControl(this.comparison.keyIndicator, [Validators.required]),
      region: new UntypedFormControl(this.comparison.region, [Validators.required]),
      ringMiles: new UntypedFormControl(this.comparison.ringMiles || 3, this.VALIDATORS_RING)
    });

    this.addSubscription(
      this.form.valueChanges
        .pipe(debounceTime(300))
        .subscribe((formValue: any) => {
          const { region, comparisonType, keyIndicator, driveTimeMinutes, ringMiles } = formValue;
          const location = this.store?.space?.location;
          const shoppingCenter = this.store?.space?.shoppingCenter;
          const enrichedMetadata = {
            region,
            comparisonType,
            keyIndicator,
            ...(region === 'DRIVE_TIME' && { driveTimeMinutes }),
            ...(region === 'RING' && { ringMiles }),
            storeId: this.store?.uuid,
            storeName: this.store?.name,
            addressLine1: location?.addressLine1,
            city: location?.city,
            cbsa: this.marketName,
            state: location?.state,
            postalCode: location?.postalCode,
            county: location?.county,
            shoppingCenterName: shoppingCenter?.name,
            landlord: shoppingCenter?.ownerCompany?.name
          };

          this.hitTrackingService.hitTheUserTracking(
            USER_TRACKING_SOURCES.COMPETITIVE_ANALYSIS_DETAILS_PAGE,
            enrichedMetadata,
            USER_TRACKING_ACTIONS.REGION_OR_COMPARISON_CHANGED,
            USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.REGION_OR_COMPARISON_CHANGED]
          ).subscribe();

          this.loadComparison();
        })
    );
  }

  private fetchUnifiedRankingData(): void {
    if (!this.store?.uuid) return;

    // Cancel any in-flight request to prevent stale data overwriting fresh data
    this.cancelPendingRequest$.next();

    this.isLoading = true;

    const comparison = new StoreComparison({
      store: this.store,
      volume: this.comparison?.volume,
      comparisonType: this.form.get('comparisonType').value || ComparisonType.ALL,
      region: this.form.get('region').value || RegionType.DRIVE_TIME,
      driveTimeMinutes: this.form.get('driveTimeMinutes').value,
      ringMiles: this.form.get('ringMiles').value,
      keyIndicator: this.form.get('keyIndicator').value || KeyIndicatorType.SALES_VOLUME,
      salesSqftDisplayType: SalesSqftDisplayType.TOTAL_AREA,
      salesVolumeDisplayType: SalesVolumeDisplayType.WEEKLY
    });

    this.storeComparisonService.findOne(comparison)
      .pipe(takeUntil(this.cancelPendingRequest$))
      .subscribe({
        next: (result: StoreComparison) => {
          // Fully replace the comparison with the result (preserving current store)
          const resultCopy = new StoreComparison(result);
          resultCopy.store = this.comparison.store;
          this.comparison = resultCopy;

          this.rankings = resultCopy.rankings || [];
          this.updateChartDataFromRankings(this.rankings);
          this.mapRankingsToTableData(this.rankings);

          // Update labels after comparison is fully updated
          this.setComparisonTypeLabel();

          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error fetching store rankings:', error);
          this.toaster.error('Unable to load ranking data. Please try again later.');
          this.rankings = [];
          this.tableData = [];
          this.isLoading = false;
        }
      });
  }

  /**
   * Updates chart view data from rankings array
   * Extracts current store's ranking and initializes chart
   */
  private updateChartDataFromRankings(rankings: StoreRanking[]): void {
    if (!rankings || rankings.length === 0) {
      this.ranking = null;
      return;
    }

    // Find current store's ranking from the rankings array
    this.ranking = rankings.find(r => r.store?.uuid === this.store.uuid);

    if (this.ranking) {
      // Update comparison object with rankings data
      if (!this.comparison) {
        this.comparison = new StoreComparison({
          store: this.store,
          comparisonType: this.form.get('comparisonType').value,
          keyIndicator: this.form.get('keyIndicator').value,
          region: this.form.get('region').value,
          driveTimeMinutes: this.form.get('driveTimeMinutes').value,
          ringMiles: this.form.get('ringMiles').value
        });
      }

      this.comparison.rankings = rankings;

      // Initialize chart descriptors
      this.labelPercentile = convertToNth(this.ranking.percentile);
      this.rankingDescriptor = buildRankingDescriptor(this.ranking);
      this.rankingGroupDescriptor = buildStoreRankingGroupDescriptor(this.comparison);
      this.rankingTypeDescriptor = buildStoreRankingTypeDescriptor(this.comparison);
      this.color = this.GRADIENT(this.ranking.percentile / 100).css();
      this.calculateCircularProgress(this.ranking.percentile);

      // Initialize region label
      this.setRegionLabel();

      // Initialize number line chart
      this.initNumChart();
    }
  }

  private mapRankingsToTableData(rankings: any[]): void {
    if (!rankings || rankings.length === 0) {
      this.tableData = [];
      return;
    }

    this.tableData = rankings.map((ranking, index) => {
      const store = ranking.store;
      const location = store?.space?.location;
      const address = location ? `${location.city || ''}${location.state ? ', ' + location.state : ''}`.trim() : '--';
      const keyIndicator = this.comparison?.keyIndicator;
      const salesVolume = ranking.volumeValue != null ?
      keyIndicator === KeyIndicatorType.SALES_SQFT ?
    `$${Number(ranking.volumeValue).toFixed(1)}M` :
    `$${(Number(ranking.volumeValue) / 1000000).toFixed(1)}M` : '--';
      const size = store?.totalArea ? store.totalArea.toLocaleString() : '--';
      const distance = ranking.distance != null ? ranking.distance.toFixed(1) : '--';
      // Use percentageSalesVsMarketArea for the last column (2 decimal places)
      const salesVsMarket = ranking.percentageSalesVsMarketArea != null
        ? `${ranking.percentageSalesVsMarketArea >= 0 ? '+' : ''}${ranking.percentageSalesVsMarketArea.toFixed(2)}%`
        : '--';

      return {
        rank: ranking.rank?.toString() || (index + 1).toString(),
        name: store?.name || '--',
        address: address,
        salesVolume: salesVolume,
        fitCategory: store?.fit ? store.fit.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : 'N/A',
        size: size,
        distance: distance,
        salesVsMarket: salesVsMarket
      };
    });
  }

  private calculateCircularProgress(percentile: number): void {
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const progress = (percentile / 100) * circumference;

    this.strokeDasharray = `${circumference} ${circumference}`;
    this.strokeDashoffset = `${circumference - progress}`;
  }

  // private initChart(): void {
  //   this.chartDataSets = [{
  //     data: [this.ranking.percentile, 100 - this.ranking.percentile],
  //     label: 'Store Ranking'
  //   }];
  //   this.chartLabels = [`${convertToNth(this.ranking.percentile)} Percentile`, null];

  //   const borderColor = this.GRADIENT(this.ranking.percentile / 100).css();
  //   const backgroundColor = borderColor.replace(')', ',0.6)');
  //   const hoverBackgroundColor = borderColor.replace(')', ',0.5)');
  //   this.chartColors = [
  //     {
  //       backgroundColor: [backgroundColor, 'rgba(0,0,0,0.1)'],
  //       borderColor: [borderColor, 'rgba(0,0,0,0)'],
  //       hoverBackgroundColor: [hoverBackgroundColor, 'rgba(0,0,0,0.1)'],
  //       borderWidth: 3
  //     }
  //   ];
  //   this.chartOptions = {
  //     cutoutPercentage: 75,
  //     tooltips: {
  //       bodyAlign: 'center',
  //       displayColors: false,
  //       custom: (tooltipModel: ChartTooltipModel) => {
  //         let index = 1;
  //         if (tooltipModel.dataPoints) {
  //           index = tooltipModel.dataPoints[0].index;
  //           tooltipModel.body[0].lines = [this.chartLabels[0]]
  //         }

  //         tooltipModel.opacity = index ? 0 : 1;
  //         tooltipModel.width = 100;
  //       }
  //     }
  //   };
  // }

  /**
   * Reloads comparison data when form values change
   * Uses unified API call to prevent duplicate requests
   */
  private loadComparison(): void {
    // Fetch unified data - this will update comparison, chart, table, and labels
    this.fetchUnifiedRankingData();
  }

  private setComparisonTypeLabel(): void {
    switch (this.comparison.comparisonType) {
      case ComparisonType.ALL:
        this.labelComparisonType = 'All Grocery Stores';
        break;
      case ComparisonType.BANNER:
        if (this.comparison.store.banner) {
          this.labelComparisonType = `All ${this.comparison.store.banner.name} Stores`;
        } else {
          this.labelComparisonType = 'All Banner Stores';
        }
        break;
      case ComparisonType.FIT_CATEGORY:
        this.definitionService.findAllFits()
          .subscribe((definitions: Definition<FitType>[]) => {
            const definition = _.find(definitions, (definition: Definition<FitType>) => definition.systemName === this.comparison.store.fit);
            if (definition) {
              this.labelComparisonType = `All ${definition.displayName} FIT Stores`;
            } else {
              this.labelComparisonType = 'All FIT Stores';
            }
          });
        break;
    }
  }

  private setRegionLabel(): void {
    switch (this.comparison.region) {
      case RegionType.DRIVE_TIME:
        this.labelRegion = `${this.comparison.driveTimeMinutes}-Minute Drive Time`;
        break;
      case RegionType.MARKET_AREA:
        // @ts-ignore
        const marketName = this.actualComparison?.geoJson?.getFeature()?.properties['NAME'];
        if (marketName) {
          this.labelRegion = `Market: ${marketName}`;
        } else {
          this.labelRegion = 'Market Area';
        }
        break;
      case RegionType.RING:
        this.labelRegion = `${this.comparison.ringMiles}-Mile Ring`;
        break;
      case RegionType.TRADE_AREA:
        this.labelRegion = 'MTN Trade Area';
        break;
    }
  }

}
